import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { 
  ShoppingBag, 
  Trash2, 
  Plus, 
  Minus, 
  ChevronRight, 
  ArrowLeft,
  ShieldCheck,
  Truck,
  CreditCard
} from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { motion, AnimatePresence } from 'motion/react';
import CheckoutModal from '../components/CheckoutModal';

export default function Cart() {
  const [, setLocation] = useLocation();
  const { items, updateQuantity, removeFromCart, totalPrice, totalItems } = useCart();
  const { user } = useAuth();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const handleCheckout = () => {
    setIsCheckoutOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1F1F] font-sans flex flex-col justify-center">
      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md w-full">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 border border-[#E5E0D8] shadow-sm"
            >
              <ShoppingBag className="w-8 h-8 text-gray-400" />
            </motion.div>
            <h1 className="text-2xl font-serif font-bold text-[#1F1F1F] mb-3 uppercase tracking-tight">Your Bag is Empty</h1>
            <p className="text-gray-500 mb-8 font-medium text-xs leading-relaxed max-w-xs mx-auto">
              You haven't added any spices yet. Explore our freshly ground collection to add to your pantry.
            </p>
            <Link href="/products">
              <button className="w-full btn-terracotta bg-[#D97736] text-white py-3.5 rounded font-bold uppercase tracking-wider text-xs shadow-md hover:bg-[#C85A17] active:scale-95 transition-all">
                Start Shopping
              </button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12 w-full pb-36 lg:pb-20">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3 mb-6 sm:mb-8 border-b border-[#E5E0D8] pb-4">
            <div>
              <Link href="/products" className="inline-flex items-center gap-1.5 text-[#D97736] hover:underline mb-2 group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-wider">Continue Shopping</span>
              </Link>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight uppercase text-[#1F1F1F]">
                Shopping <span className="text-[#C85A17]">Bag</span>
              </h1>
            </div>
            <div className="flex items-center gap-4 text-gray-500 text-xs font-semibold">
              <span>{totalItems} item{totalItems > 1 ? 's' : ''} in Bag</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
            {/* Cart Items List */}
            <div className="lg:col-span-8 space-y-3">
              <AnimatePresence mode="popLayout">
                {items.map((item) => (
                  <motion.div 
                    key={item.cartItemId}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-white border border-[#E5E0D8] rounded-lg p-4 sm:p-5 flex flex-row gap-4 items-center group relative shadow-sm"
                  >
                    {/* Item Image */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded p-1 flex-shrink-0 border border-gray-100 flex items-center justify-center">
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-bold text-[#1F1F1F] mb-1 truncate">
                            {item.name}
                          </h3>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-[#FAF7F2] border border-[#E5E0D8] rounded text-[10px] font-bold text-gray-600">
                              Size: {item.selectedWeight}
                            </span>
                            <span className="text-[11px] font-bold text-gray-500">
                              Rs. {item.price.toFixed(2)} / pack
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.cartItemId)}
                          aria-label={`Remove ${item.name} from bag`}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        {/* Quantity Selector */}
                        <div className="flex items-center border border-gray-300 rounded h-8 bg-white">
                          <button 
                            onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                            aria-label="Decrease quantity"
                            className="w-7 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-bold text-xs text-[#1F1F1F]">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                            aria-label="Increase quantity"
                            className="w-7 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Line Total */}
                        <div className="text-right">
                          <span className="text-sm sm:text-base font-black text-[#1F1F1F]">
                            Rs. {(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-5 sm:p-6 shadow-sm">
                <h2 className="text-base font-bold text-[#1F1F1F] mb-5 uppercase tracking-wider border-b border-[#E5E0D8] pb-3">
                  Order Summary
                </h2>

                <div className="space-y-3.5 mb-6 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-semibold">Subtotal</span>
                    <span className="font-bold text-[#1F1F1F]">Rs. {totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-semibold">Shipping</span>
                    <span className="font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded text-[10px] uppercase">Free</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-semibold">Estimated Taxes</span>
                    <span className="text-gray-400 italic">Included</span>
                  </div>
                  
                  <div className="pt-4 border-t border-[#E5E0D8] flex justify-between items-center text-sm">
                    <span className="font-bold text-[#1F1F1F]">Total Amount</span>
                    <span className="text-xl font-extrabold text-[#D97736]">Rs. {totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  onClick={handleCheckout}
                  className="w-full btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white py-3.5 rounded font-bold uppercase tracking-wider text-xs shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  Proceed to Checkout
                  <ChevronRight className="w-4 h-4" />
                </button>

                <div className="mt-6 pt-5 border-t border-[#E5E0D8] grid grid-cols-2 gap-3 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <ShieldCheck className="w-4 h-4 text-gray-400" />
                    <span className="text-[9px] font-bold uppercase text-gray-500">100% Purity</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Truck className="w-4 h-4 text-gray-400" />
                    <span className="text-[9px] font-bold uppercase text-gray-500">Direct Dispatch</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} />
    </div>
  );
}
